import React, { useState, useEffect } from 'react';

interface EmailConfig {
  provider: 'smtp' | 'ses' | 'sendgrid';
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  sesRegion?: string;
  sesAccessKey?: string;
  sesSecretKey?: string;
  sendgridApiKey?: string;
}

const STORAGE_KEY = 'email_config';

const loadConfig = (): EmailConfig => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load email config:', error);
  }
  return {
    provider: 'smtp',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: true,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: 'noreply@gustonltd.com',
    fromName: 'Guston Portal',
    replyToEmail: 'support@gustonltd.com',
  };
};

export const EmailSettings: React.FC = () => {
  const [config, setConfig] = useState<EmailConfig>(loadConfig());

  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    const loaded = loadConfig();
    setConfig(loaded);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      await new Promise(resolve => setTimeout(resolve, 500));
      alert('Email settings saved successfully! Configuration stored locally.');
    } catch (error) {
      alert('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      alert('Please enter a test email address');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      // TODO: API call to send test email
      await new Promise(resolve => setTimeout(resolve, 2000));
      setTestResult({
        success: true,
        message: `Test email sent successfully to ${testEmail}`,
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to send test email. Please check your configuration.',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Configuration</h2>
        <p className="text-gray-600">Configure SMTP or email service provider for sending notifications</p>
      </div>

      {/* Provider Selection */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Email Provider
        </label>
        <div className="grid grid-cols-3 gap-4">
          {(['smtp', 'ses', 'sendgrid'] as const).map(provider => (
            <button
              key={provider}
              onClick={() => setConfig({ ...config, provider })}
              className={`p-4 border-2 rounded-lg transition-all ${
                config.provider === provider
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="text-lg font-semibold capitalize">{provider.toUpperCase()}</div>
              <div className="text-xs text-gray-600 mt-1">
                {provider === 'smtp' && 'Custom SMTP Server'}
                {provider === 'ses' && 'Amazon SES'}
                {provider === 'sendgrid' && 'SendGrid API'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* SMTP Configuration */}
      {config.provider === 'smtp' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">SMTP Server Configuration</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host *
              </label>
              <input
                type="text"
                value={config.smtpHost}
                onChange={(e) => setConfig({ ...config, smtpHost: e.target.value })}
                placeholder="smtp.gmail.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port *
              </label>
              <select
                value={config.smtpPort}
                onChange={(e) => setConfig({ ...config, smtpPort: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              >
                <option value="25">25 (Plain)</option>
                <option value="587">587 (TLS - Recommended)</option>
                <option value="465">465 (SSL)</option>
                <option value="2525">2525 (Alternative)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username *
              </label>
              <input
                type="text"
                value={config.smtpUser}
                onChange={(e) => setConfig({ ...config, smtpUser: e.target.value })}
                placeholder="your-email@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <input
                type="password"
                value={config.smtpPassword}
                onChange={(e) => setConfig({ ...config, smtpPassword: e.target.value })}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={config.smtpSecure}
              onChange={(e) => setConfig({ ...config, smtpSecure: e.target.checked })}
              className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              Use TLS/SSL (Recommended)
            </label>
          </div>
        </div>
      )}

      {/* AWS SES Configuration */}
      {config.provider === 'ses' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">AWS SES Configuration</h3>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex">
              <div className="text-blue-600 mr-3">ℹ️</div>
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">AWS SES Setup Required:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Verify your domain or email address in AWS SES</li>
                  <li>Request production access (if sending to non-verified emails)</li>
                  <li>Create IAM user with SES send permissions</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AWS Region *
              </label>
              <select
                value={config.sesRegion || 'us-west-2'}
                onChange={(e) => setConfig({ ...config, sesRegion: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AWS Access Key ID *
              </label>
              <input
                type="text"
                value={config.sesAccessKey || ''}
                onChange={(e) => setConfig({ ...config, sesAccessKey: e.target.value })}
                placeholder="AKIAIOSFODNN7EXAMPLE"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AWS Secret Access Key *
              </label>
              <input
                type="password"
                value={config.sesSecretKey || ''}
                onChange={(e) => setConfig({ ...config, sesSecretKey: e.target.value })}
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* SendGrid Configuration */}
      {config.provider === 'sendgrid' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">SendGrid Configuration</h3>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex">
              <div className="text-purple-600 mr-3">ℹ️</div>
              <div className="text-sm text-purple-800">
                <p className="font-semibold mb-1">SendGrid API Key:</p>
                <p>Get your API key from SendGrid Dashboard → Settings → API Keys</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SendGrid API Key *
            </label>
            <input
              type="password"
              value={config.sendgridApiKey || ''}
              onChange={(e) => setConfig({ ...config, sendgridApiKey: e.target.value })}
              placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Email Sender Configuration */}
      <div className="space-y-4 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Email Sender Details</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Email *
            </label>
            <input
              type="email"
              value={config.fromEmail}
              onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
              placeholder="noreply@gustonltd.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Email address that appears in the "From" field</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Name *
            </label>
            <input
              type="text"
              value={config.fromName}
              onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
              placeholder="Guston Portal"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Name that appears in the "From" field</p>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reply-To Email
            </label>
            <input
              type="email"
              value={config.replyToEmail}
              onChange={(e) => setConfig({ ...config, replyToEmail: e.target.value })}
              placeholder="support@gustonltd.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Email address for replies</p>
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Email Configuration</h3>
        <p className="text-sm text-gray-600 mb-4">
          Send a test email to verify your configuration is working correctly.
        </p>

        <div className="flex gap-4">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
          />
          <button
            onClick={handleTestEmail}
            disabled={testing}
            className="px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50"
          >
            {testing ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>

        {testResult && (
          <div className={`mt-4 p-4 rounded-lg ${
            testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {testResult.message}
            </p>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors shadow-sm disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving...' : 'Save Email Settings'}
        </button>
      </div>
    </div>
  );
};
